/**
 * Shared AI Writing Assistant panel (PBI #1152).
 *
 * Same UI for e-card and mailed modes. Mode forks:
 * - e-card: subject line + SSE streaming + cardTemplateId
 * - mailed: body maxLength, non-streaming /generate only
 *
 * Studio layout (desktop sm+):
 *   TOP    — Tone + Notes (outside the generate group)
 *   BORDERED WORKSPACE — 55% controls (defaults + Generate + rewrite),
 *                        45% draft column stretched to match controls height
 * Mobile (base): same sections stacked inside one bordered workspace.
 *
 * AppModal chrome lives in thin wrappers (sticky-footer id literals).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Anchor,
  Badge,
  Button,
  Checkbox,
  Group,
  Loader,
  Paper,
  SegmentedControl,
  Select,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  Textarea,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import {
  IconAlertCircle,
  IconAlertTriangle,
  IconBulb,
  IconCheck,
  IconInfoCircle,
  IconRefresh,
  IconRestore,
  IconSparkles,
  IconWand,
  IconX,
} from '@tabler/icons-react';
import type { AxiosError } from 'axios';
import axios from 'axios';

import { useCardAttributes } from '../../hooks/useCardAttributes';
import {
  useAiWritingGeneration,
  type AiWritingMode,
} from '../../hooks/useAiWritingGeneration';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { useUpgradeModal } from '../../contexts/UpgradeModalContext';
import { updateContact } from '../../services/contactsService';
import { validateContactNotes } from '../../utils/contactTextValidation';
import { buildPartialContactUpdate } from '../../utils/contactWritePayload';
import { getSubscriptionStatus } from '../../services/subscriptionService';
import {
  resetAiGuidanceToDefault,
  checkInstructionsQuality,
  updateAiSettings,
  INSTRUCTIONS_QUALITY_MIN_CHARS,
  GreetingGenerationModes,
} from '../../services/greetingGenerationService';
import { actionColors, brandColors } from '../../config/theme';
import { resolveKnownPlaceholders, type TemplateDisplayContext } from '../../utils/templateRenderer';
import classes from './AiWritingAssistantPanel.module.css';

/** Matches AppModal's mobile full-screen breakpoint (Mantine xs). */
const MOBILE_LAYOUT_QUERY = '(max-width: 576px)';

const TONE_PRIMARY_OPTIONS = ['Heartfelt', 'Funny', 'Teasing'] as const;
const MORE_TONE_SENTINEL = '__more__';

/**
 * Popover settings for the overflow tone menu ("More tones...").
 *
 * On a phone-width AI Writing Assistant, Mantine's default absolute popover
 * is portaled onto document.body and extends the document scroll width. The
 * full-width field grows to match, and the menu copies that width (about
 * 156px wider each frame) until it is hidden and the cycle starts again.
 * That flicker is what people see when they tap More tones on an iPhone SE.
 * Fixed positioning keeps the menu out of scroll width so the list stays
 * put and a tone can be chosen. Desktop widths already fit in the viewport.
 */
export const AI_TONE_OVERFLOW_COMBOBOX_PROPS = {
  floatingStrategy: 'fixed',
} as const;

const COACHING_DISMISSED_KEY = 'aiAssistant.coachingDismissed';

/**
 * Refinement chip labels only. The API maps each label to canonical instruction text
 * via AiRefinementChips (must stay in sync). Do not send ad-hoc English here — that
 * bypasses the server mapping and weaker copy used to drop poem structure.
 */
const REFINEMENT_CHIPS: ReadonlyArray<{ label: string }> = [
  { label: 'Make shorter' },
  { label: 'Be funnier' },
  { label: 'Polish' },
];

/**
 * Fixed copy when the paid instructions quality check flags time-bound wording.
 * The API returns only needsWarning + confidence — the sentence lives here so we
 * never stream user-authored text back from the classifier endpoint.
 */
export const INSTRUCTIONS_QUALITY_WARNING_COPY =
  'These instructions may not work next year. Try removing specific years, ages, or one-time events so the same instructions still fit in a year.';

/** Latest generation attempt remembered so "Try again" can replay it. */
interface LastRun {
  mode: 'suggest' | 'rewrite';
  instructions?: string;
}

export interface AiWritingAssistantPanelProps {
  /** Channel: e-card (subject + stream) vs mailed (body-only, non-stream). */
  writingMode: AiWritingMode;

  opened: boolean;
  onClose: () => void;

  contactId: number;
  contactFirstName: string;
  contactLastName: string;
  eventTypeId: number;
  eventTypeName: string;
  /** Required for e-card generation. */
  cardTemplateId?: number;
  eventDate?: string;
  /** Cardly / mailed panel character cap. */
  maxBodyLength?: number;

  /** Subject currently shown in the parent editor (e-card). Ignored for mailed. */
  initialSubjectLine?: string;
  /** Greeting / message currently shown in the parent editor. */
  initialGreetingBody: string;

  /** Current AI-related settings from the parent. */
  initialNotes: string;
  initialAiInstructions: string;
  initialTone: string;
  initialIncludeNotesInAiPrompt: boolean;

  onNotesSaved?: (notes: string) => void;
  onAiSettingsSaved?: (next: {
    aiGuidance: string | null;
    preferredTone: string | null;
    includeNotesInAiPrompt: boolean;
  }) => void;

  /** Commit handler — wrappers map mailed to body-only insert. */
  onInsert: (next: { subjectLine: string; greetingBody: string }) => void;

  availableTones?: ReadonlyArray<{ value: string; label: string }>;

  /** E-card placeholder substitution context. */
  displayContext?: TemplateDisplayContext;

  /**
   * Reports Cancel/Insert enablement to the AppModal wrapper (sticky footer).
   * Called whenever generation or dirty state changes.
   */
  onFooterStateChange: (state: AiWritingAssistantFooterState) => void;
}

/** Footer controls owned by AppModal wrappers. */
export interface AiWritingAssistantFooterState {
  isGenerating: boolean;
  canInsert: boolean;
  onInsert: () => void;
  onClose: () => void;
}

/**
 * Returns a stable snapshot used to detect "dirty" subject/greeting edits.
 * Two snapshots compare equal iff both fields contain identical strings.
 */
function snapshotsEqual(
  a: { subjectLine: string; greetingBody: string },
  b: { subjectLine: string; greetingBody: string }
): boolean {
  return a.subjectLine === b.subjectLine && a.greetingBody === b.greetingBody;
}

export const AiWritingAssistantPanel = ({
  writingMode,
  opened,
  onClose,
  contactId,
  contactFirstName,
  contactLastName,
  eventTypeId,
  eventTypeName,
  cardTemplateId,
  eventDate,
  maxBodyLength,
  initialSubjectLine = '',
  initialGreetingBody,
  initialNotes,
  initialAiInstructions,
  initialTone,
  initialIncludeNotesInAiPrompt,
  onNotesSaved,
  onAiSettingsSaved,
  onInsert,
  availableTones,
  displayContext,
  onFooterStateChange,
}: AiWritingAssistantPanelProps) => {
  const { refreshSubscription } = useSubscription();
  const { registerOnClose, openUpgrade } = useUpgradeModal();
  const { tones: hookTones } = useCardAttributes();
  const tonesList = availableTones ?? hookTones;
  const isEcard = writingMode === GreetingGenerationModes.ECard;
  const isMobileLayout = useMediaQuery(MOBILE_LAYOUT_QUERY, false, {
    getInitialValueInEffect: false,
  });

  // ── Resolved initial values (placeholder substitution applied) ─────
  const resolvedInitialSubject = useMemo(
    () => (displayContext ? resolveKnownPlaceholders(initialSubjectLine, displayContext) : initialSubjectLine),
    [initialSubjectLine, displayContext]
  );
  const resolvedInitialGreeting = useMemo(
    () => (displayContext ? resolveKnownPlaceholders(initialGreetingBody, displayContext) : initialGreetingBody),
    [initialGreetingBody, displayContext]
  );

  // ── Output state ────────────────────────────────────────────────────
  const [subjectLine, setSubjectLine] = useState(resolvedInitialSubject);
  const [greetingBody, setGreetingBody] = useState(resolvedInitialGreeting);

  // Reset output state every time the modal opens — otherwise stale text
  // from a previous open lingers when the parent passes different defaults.
  useEffect(() => {
    if (opened) {
      setSubjectLine(resolvedInitialSubject);
      setGreetingBody(resolvedInitialGreeting);
      setLastRun(null);
      setOverwriteSnapshot(null);
      setError(null);
      setOccasionHintWarnings([]);
      setRewriteInstructions('');
      setShowInstructionsQualityWarning(false);
      setShowFreePlanQualityNudge(false);
      lastQualityCheckedKeyRef.current = null;
      skipFurtherQualityChecksRef.current = false;
      qualityCheckAbortRef.current?.abort();
      qualityCheckAbortRef.current = null;
      hasRequestedOpenQualityRef.current = false;
    }
    // We intentionally depend on `opened` only: reopening the modal restarts the session.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened]);

  // ── Inputs state (Tone + Notes + Instructions + Include-Notes) ──────
  const [tone, setTone] = useState(initialTone);
  useEffect(() => {
    if (opened) setTone(initialTone);
  }, [opened, initialTone]);

  const [notes, setNotes] = useState(initialNotes);
  const [notesDirty, setNotesDirty] = useState(false);
  const [notesFieldError, setNotesFieldError] = useState<string | null>(null);
  const [savingNotes, setSavingNotes] = useState(false);
  useEffect(() => {
    if (opened) {
      setNotes(initialNotes);
      setNotesDirty(false);
    }
  }, [opened, initialNotes]);

  const [aiInstructions, setAiInstructions] = useState(initialAiInstructions);
  const [savingInstructions, setSavingInstructions] = useState(false);
  const lastSavedInstructionsRef = useRef(initialAiInstructions);
  const latestInstructionsSaveRef = useRef<Promise<void>>(Promise.resolve());
  const lastQualityCheckedKeyRef = useRef<string | null>(null);
  const skipFurtherQualityChecksRef = useRef(false);
  const qualityCheckAbortRef = useRef<AbortController | null>(null);
  const hasRequestedOpenQualityRef = useRef(false);
  const [rewriteInstructions, setRewriteInstructions] = useState('');
  const [showInstructionsQualityWarning, setShowInstructionsQualityWarning] = useState(false);
  const [showFreePlanQualityNudge, setShowFreePlanQualityNudge] = useState(false);
  /** Latest default instructions — read inside timers so open-backfill matches live edits. */
  const aiInstructionsForQualityRef = useRef(aiInstructions);
  aiInstructionsForQualityRef.current = aiInstructions;
  useEffect(() => {
    if (opened) {
      setAiInstructions(initialAiInstructions);
      lastSavedInstructionsRef.current = initialAiInstructions;
    }
  }, [opened, initialAiInstructions]);

  const [includeNotes, setIncludeNotes] = useState(initialIncludeNotesInAiPrompt);
  useEffect(() => {
    if (opened) setIncludeNotes(initialIncludeNotesInAiPrompt);
  }, [opened, initialIncludeNotesInAiPrompt]);

  // ── Generation state (shared hook owns transport + empty-context contract)
  const [lastRun, setLastRun] = useState<LastRun | null>(null);
  const [overwriteSnapshot, setOverwriteSnapshot] = useState<{
    subjectLine: string;
    greetingBody: string;
  } | null>(null);
  const runGenerationRef = useRef<(run: LastRun) => Promise<void>>(async () => {});
  const pendingReplayRef = useRef<LastRun | null>(null);

  const {
    isGenerating,
    error,
    setError,
    occasionHintWarnings,
    setOccasionHintWarnings,
    runGeneration: runSharedGeneration,
    cancel: cancelSharedGeneration,
  } = useAiWritingGeneration({
    writingMode,
    contactId,
    eventTypeId,
    cardTemplateId,
    eventDate,
    maxBodyLength,
    onPaymentRequired: () => {
      registerOnClose(() => {
        void (async () => {
          try {
            await refreshSubscription();
          } catch (err) {
            console.error(
              '[AiWritingAssistant] Failed to refresh subscription after upgrade modal closed:',
              err,
            );
          }

          const replay = pendingReplayRef.current;
          pendingReplayRef.current = null;

          if (!replay) {
            return;
          }

          // Dismissing the upgrade modal without subscribing must not replay
          // generation — that immediately 402s and reopens the modal (feels
          // like the close button is broken). Replay only after a real upgrade.
          let subscribed = false;
          try {
            const status = await getSubscriptionStatus(true);
            subscribed = status.isSubscribed;
          } catch {
            subscribed = false;
          }

          if (!subscribed) {
            return;
          }

          await runGenerationRef.current(replay);
        })();
      });
    },
  });

  // ── Coaching alert visibility ───────────────────────────────────────
  const allInputsEmpty =
    !initialNotes?.trim() && !initialAiInstructions?.trim() && !aiInstructions?.trim();
  const [showCoaching, setShowCoaching] = useState(false);
  useEffect(() => {
    if (!opened) return;
    try {
      const dismissed = sessionStorage.getItem(COACHING_DISMISSED_KEY) === 'true';
      setShowCoaching(!dismissed && allInputsEmpty);
    } catch {
      setShowCoaching(allInputsEmpty);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened]);

  const dismissCoaching = useCallback(() => {
    setShowCoaching(false);
    try {
      sessionStorage.setItem(COACHING_DISMISSED_KEY, 'true');
    } catch {
      // sessionStorage unavailable (private mode) — ignore.
    }
  }, []);

  // ── Tone change handlers ────────────────────────────────────────────
  const isMoreTone = !TONE_PRIMARY_OPTIONS.includes(tone as (typeof TONE_PRIMARY_OPTIONS)[number]);
  const overflowToneOptions = useMemo(
    () =>
      tonesList
        .filter((t) => !TONE_PRIMARY_OPTIONS.includes(t.value as (typeof TONE_PRIMARY_OPTIONS)[number]))
        .map((t) => ({ value: t.value, label: t.label })),
    [tonesList]
  );

  const handleSaveToneAsDefault = useCallback(async () => {
    try {
      await updateAiSettings(contactId, eventTypeId, { preferredTone: tone });
      onAiSettingsSaved?.({
        aiGuidance: lastSavedInstructionsRef.current || null,
        preferredTone: tone || null,
        includeNotesInAiPrompt: includeNotes,
      });
      notifications.show({
        title: 'Saved',
        message: `Tone saved as default for ${contactFirstName}'s ${eventTypeName} cards.`,
        color: brandColors.success,
      });
    } catch (err) {
      const axiosErr = err as AxiosError;
      if (axiosErr?.response?.status !== 402) {
        notifications.show({
          title: 'Could not save tone',
          message: 'Your default tone was not updated. Please try again.',
          color: brandColors.danger,
        });
      }
    }
  }, [contactId, eventTypeId, tone, includeNotes, contactFirstName, eventTypeName, onAiSettingsSaved]);

  const recipientDisplayName = useMemo(
    () => (contactFirstName?.trim() ? contactFirstName.trim() : 'this contact'),
    [contactFirstName]
  );
  const eventDisplayName = useMemo(
    () => (eventTypeName?.trim() ? eventTypeName.trim() : 'this card'),
    [eventTypeName]
  );

  // ── Instructions auto-save (500ms debounce) ─────────────────────────
  useEffect(() => {
    if (!opened) return;
    if (aiInstructions === lastSavedInstructionsRef.current) return;
    const timer = window.setTimeout(() => {
      const savePromise = (async () => {
        setSavingInstructions(true);
        try {
          await updateAiSettings(contactId, eventTypeId, {
            aiGuidance: aiInstructions.trim() || null,
          });
          lastSavedInstructionsRef.current = aiInstructions;
          onAiSettingsSaved?.({
            aiGuidance: aiInstructions.trim() || null,
            preferredTone: tone || null,
            includeNotesInAiPrompt: includeNotes,
          });
        } catch (err) {
          const axiosErr = err as AxiosError;
          if (axiosErr?.response?.status !== 402) {
            notifications.show({
              title: 'Could not save instructions',
              message: 'Your changes were not saved. Please try again.',
              color: brandColors.danger,
            });
          }
        } finally {
          setSavingInstructions(false);
        }
      })();
      latestInstructionsSaveRef.current = savePromise;
    }, 500);
    return () => window.clearTimeout(timer);
  }, [aiInstructions, opened, contactId, eventTypeId, tone, includeNotes, onAiSettingsSaved]);

  /**
   * Paid-only classifier: runs after the debounced save settles (blur) and once
   * on open when the server-provided instructions are long enough. Free plans
   * get 402 with the upgrade header suppressed — we show a blue inline nudge.
   */
  const runInstructionsQualityCheck = useCallback(
    async (_source: 'open' | 'blur', overrideText?: string) => {
      if (!opened || skipFurtherQualityChecksRef.current) return;
      const trimmed = (overrideText ?? aiInstructions).trim();
      if (trimmed.length < INSTRUCTIONS_QUALITY_MIN_CHARS) {
        setShowInstructionsQualityWarning(false);
        setShowFreePlanQualityNudge(false);
        return;
      }
      const key = `${eventTypeId}:${trimmed}`;
      if (lastQualityCheckedKeyRef.current === key) return;

      await latestInstructionsSaveRef.current.catch(() => {});

      qualityCheckAbortRef.current?.abort();
      const controller = new AbortController();
      qualityCheckAbortRef.current = controller;

      try {
        const res = await checkInstructionsQuality(
          { contactId, eventTypeId, instructions: trimmed },
          { signal: controller.signal }
        );
        if (controller.signal.aborted) return;
        lastQualityCheckedKeyRef.current = key;
        setShowInstructionsQualityWarning(!!res.needsWarning);
        setShowFreePlanQualityNudge(false);
      } catch (err) {
        if (axios.isCancel(err)) return;
        const axiosErr = err as AxiosError;
        if (axiosErr?.response?.status === 402) {
          if (!controller.signal.aborted) {
            skipFurtherQualityChecksRef.current = true;
            lastQualityCheckedKeyRef.current = key;
            setShowInstructionsQualityWarning(false);
            setShowFreePlanQualityNudge(true);
          }
        } else if (!controller.signal.aborted) {
          setShowInstructionsQualityWarning(false);
          setShowFreePlanQualityNudge(false);
        }
      }
    },
    [opened, aiInstructions, contactId, eventTypeId]
  );

  useEffect(() => {
    if (!opened) return;
    if (hasRequestedOpenQualityRef.current || skipFurtherQualityChecksRef.current) return;
    if (initialAiInstructions.trim().length < INSTRUCTIONS_QUALITY_MIN_CHARS) return;
    hasRequestedOpenQualityRef.current = true;
    const timer = window.setTimeout(() => {
      const live = aiInstructionsForQualityRef.current.trim();
      if (live.length < INSTRUCTIONS_QUALITY_MIN_CHARS) return;
      void runInstructionsQualityCheck('open', live);
    }, 0);
    return () => window.clearTimeout(timer);
  }, [opened, initialAiInstructions, runInstructionsQualityCheck]);

  const handleAiInstructionsChange = useCallback((value: string) => {
    qualityCheckAbortRef.current?.abort();
    setShowInstructionsQualityWarning(false);
    setShowFreePlanQualityNudge(false);
    lastQualityCheckedKeyRef.current = null;
    setAiInstructions(value);
  }, []);

  const handleAiInstructionsBlur = useCallback(async () => {
    await latestInstructionsSaveRef.current.catch(() => {});
    await runInstructionsQualityCheck('blur');
  }, [runInstructionsQualityCheck]);

  // ── Include-Notes toggle (auto-save on change) ──────────────────────
  const handleIncludeNotesChange = useCallback(
    async (next: boolean) => {
      setIncludeNotes(next);
      try {
        await updateAiSettings(contactId, eventTypeId, { includeNotesInAiPrompt: next });
        onAiSettingsSaved?.({
          aiGuidance: lastSavedInstructionsRef.current.trim() || null,
          preferredTone: tone || null,
          includeNotesInAiPrompt: next,
        });
      } catch (err) {
        const axiosErr = err as AxiosError;
        if (axiosErr?.response?.status !== 402) {
          notifications.show({
            title: 'Could not save preference',
            message: 'The Include-in-AI setting was not saved. Please try again.',
            color: brandColors.danger,
          });
        }
      }
    },
    [contactId, eventTypeId, tone, onAiSettingsSaved]
  );

  // ── Notes manual save ───────────────────────────────────────────────
  const handleNotesSave = useCallback(async () => {
    const clientError = validateContactNotes(notes);
    if (clientError) {
      setNotesFieldError(clientError);
      return;
    }

    setNotesFieldError(null);
    setSavingNotes(true);
    try {
      await updateContact(
        contactId,
        buildPartialContactUpdate(
          { firstName: contactFirstName, lastName: contactLastName },
          { notes },
        ),
      );
      onNotesSaved?.(notes);
      setNotesDirty(false);
      notifications.show({
        title: 'Notes saved',
        message: `Saved to ${recipientDisplayName}'s contact.`,
        color: brandColors.success,
      });
    } catch (err) {
      const axiosErr = err as AxiosError<{ error?: string; fieldErrors?: Record<string, string> }>;
      if (axiosErr?.response?.status !== 402) {
        const serverNotesError =
          axiosErr?.response?.data?.fieldErrors?.notes ?? axiosErr?.response?.data?.error;
        if (serverNotesError) {
          setNotesFieldError(serverNotesError);
        }

        notifications.show({
          title: 'Could not save notes',
          message: serverNotesError ?? 'Your notes were not saved. Please try again.',
          color: brandColors.danger,
        });
      }
    } finally {
      setSavingNotes(false);
    }
  }, [contactId, notes, contactFirstName, contactLastName, recipientDisplayName, onNotesSaved]);

  // ── Generation core (Generate new / Rewrite / refinement chip / Try again)
  const runGeneration = useCallback(
    async (run: LastRun) => {
      const dirty =
        subjectLine !== resolvedInitialSubject || greetingBody !== resolvedInitialGreeting;
      const snapshot = dirty ? { subjectLine, greetingBody } : overwriteSnapshot;
      if (dirty) {
        setOverwriteSnapshot({ subjectLine, greetingBody });
      }

      setLastRun(run);
      pendingReplayRef.current = run;
      setSubjectLine('');
      setGreetingBody('');

      const result = await runSharedGeneration({
        runMode: run.mode,
        tone: tone || undefined,
        instructions: run.instructions,
        existingGreetingBody: run.mode === 'rewrite' ? greetingBody : undefined,
        preferStream: isEcard,
        onStreamDelta: (partial) => {
          if (isEcard) {
            setSubjectLine(partial.subjectLine);
          }
          setGreetingBody(partial.greetingBody);
        },
      });

      if (result) {
        if (isEcard) {
          setSubjectLine(result.subjectLine);
        }
        setGreetingBody(result.greetingBody);
        return;
      }

      // Failed / aborted / 402 — restore pre-generation text so the user does not see blanks.
      if (snapshot) {
        setSubjectLine(snapshot.subjectLine);
        setGreetingBody(snapshot.greetingBody);
      } else {
        setSubjectLine(resolvedInitialSubject);
        setGreetingBody(resolvedInitialGreeting);
      }
    },
    [
      subjectLine,
      greetingBody,
      resolvedInitialSubject,
      resolvedInitialGreeting,
      tone,
      overwriteSnapshot,
      runSharedGeneration,
      isEcard,
    ]
  );

  runGenerationRef.current = runGeneration;

  const handleGenerateNew = useCallback(() => runGeneration({ mode: 'suggest' }), [runGeneration]);
  const handleRewrite = useCallback(
    () => runGeneration({ mode: 'rewrite', instructions: rewriteInstructions.trim() || undefined }),
    [runGeneration, rewriteInstructions]
  );
  const handleTryAgain = useCallback(() => {
    if (!lastRun) return;
    runGeneration(lastRun);
  }, [lastRun, runGeneration]);

  const handleRefinementChip = useCallback(
    (chipLabel: string) => runGeneration({ mode: 'rewrite', instructions: chipLabel }),
    [runGeneration]
  );

  const handleCancelStream = useCallback(() => {
    cancelSharedGeneration();
  }, [cancelSharedGeneration]);

  const handleRestoreEdits = useCallback(() => {
    if (!overwriteSnapshot) return;
    setSubjectLine(overwriteSnapshot.subjectLine);
    setGreetingBody(overwriteSnapshot.greetingBody);
    setOverwriteSnapshot(null);
    setOccasionHintWarnings([]);
  }, [overwriteSnapshot]);

  const handleInsert = useCallback(() => {
    onInsert({ subjectLine, greetingBody });
    onClose();
  }, [subjectLine, greetingBody, onInsert, onClose]);

  const handleClose = useCallback(() => {
    cancelSharedGeneration();
    qualityCheckAbortRef.current?.abort();
    onClose();
  }, [cancelSharedGeneration, onClose]);

  // ── Derived UI state ────────────────────────────────────────────────
  const rewriteDisabled =
    !greetingBody.trim() || isGenerating || !rewriteInstructions.trim();
  const generateNewDisabled = isGenerating;
  const refinementDisabled = isGenerating || !lastRun;
  const insertEdited = !snapshotsEqual(
    { subjectLine, greetingBody },
    { subjectLine: resolvedInitialSubject, greetingBody: resolvedInitialGreeting }
  );

  const segmentedData = [
    ...TONE_PRIMARY_OPTIONS.map((t) => ({ label: t, value: t })),
    { label: 'More...', value: MORE_TONE_SENTINEL },
  ];

  useEffect(() => {
    onFooterStateChange({
      isGenerating,
      canInsert: insertEdited,
      onInsert: handleInsert,
      onClose: handleClose,
    });
  }, [isGenerating, insertEdited, handleInsert, handleClose, onFooterStateChange]);

  return (
    <Stack gap="md">
      {showCoaching && (
        <Alert
          color="brand"
          variant="light"
          withCloseButton
          onClose={dismissCoaching}
          icon={<IconBulb size={16} />}
        >
          Tell the AI a bit about {recipientDisplayName} in the Notes section below —
          their interests, dietary needs, anything you want remembered across
          cards. Even one sentence makes the message feel more personal.
        </Alert>
      )}

      {/* ── TOP: shared context (tone + notes) ───────────────────────── */}
      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
        <Stack gap="xs">
          <Text size="sm" fw={600}>Tone</Text>
          <Group gap="xs" wrap="nowrap" align="center">
            <SegmentedControl
              value={isMoreTone ? MORE_TONE_SENTINEL : tone}
              onChange={(value) => {
                if (value === MORE_TONE_SENTINEL) return;
                setTone(value);
              }}
              data={segmentedData}
              size="sm"
              fullWidth
            />
          </Group>
          {isMoreTone || overflowToneOptions.length > 0 ? (
            <Select
              size="sm"
              data={overflowToneOptions}
              value={isMoreTone ? tone : null}
              placeholder="More tones..."
              clearable
              searchable
              comboboxProps={AI_TONE_OVERFLOW_COMBOBOX_PROPS}
              onChange={(value) => {
                if (value) setTone(value);
              }}
            />
          ) : null}
          <Group gap="xs">
            <Text size="xs" c="dimmed">Just for this card.</Text>
            <Anchor size="xs" onClick={handleSaveToneAsDefault} component="button" type="button">
              Save as default for {recipientDisplayName}&apos;s {eventDisplayName} cards
            </Anchor>
          </Group>
        </Stack>

        <Stack gap="xs">
          <Group justify="space-between" align="center" wrap="wrap">
            <Text size="sm" fw={600}>Notes about {recipientDisplayName}</Text>
            <Group gap="sm">
              <Checkbox
                size="xs"
                label="Include in AI"
                checked={includeNotes}
                onChange={(e) => handleIncludeNotesChange(e.currentTarget.checked)}
              />
              <Button
                size="compact-xs"
                variant="light"
                disabled={!notesDirty || savingNotes}
                onClick={handleNotesSave}
                loading={savingNotes}
              >
                Save to contact
              </Button>
            </Group>
          </Group>
          <Textarea
            value={notes}
            onChange={(e) => {
              const next = e.currentTarget.value;
              setNotes(next);
              setNotesDirty(true);
              setNotesFieldError(validateContactNotes(next));
            }}
            error={notesFieldError}
            placeholder={`Stable facts about ${recipientDisplayName}: interests, dietary needs, anything you want remembered.`}
            autosize
            minRows={3}
            maxRows={6}
            styles={{ input: { opacity: includeNotes ? 1 : 0.6 } }}
          />
        </Stack>
      </SimpleGrid>

      {/* ── WORKSPACE: 55% controls + 45% draft stretched to Rewrite baseline ── */}
      <Paper withBorder p="md" radius="md">
        <div className={classes.workspaceGrid}>
          <Stack gap="md">
              <Stack gap="xs">
                <Text size="sm" fw={600}>Default instructions for new messages (optional)</Text>
                <Text size="xs" c="dimmed">
                  These apply whenever you generate a new greeting for {recipientDisplayName}&apos;s{' '}
                  {eventDisplayName} cards. Use Rewrite below for one-off tweaks to the draft.
                </Text>
                <Textarea
                  value={aiInstructions}
                  onChange={(e) => handleAiInstructionsChange(e.currentTarget.value)}
                  onBlur={() => void handleAiInstructionsBlur()}
                  placeholder='e.g. "Reference our annual fishing trip" or "Keep it short and punny"'
                  autosize
                  minRows={2}
                  maxRows={5}
                  maxLength={1000}
                />
                <Text size="xs" c="dimmed">
                  Saved as default for {recipientDisplayName}&apos;s {eventDisplayName} cards
                  {savingInstructions ? ' · Saving...' : ''}
                </Text>

                {showInstructionsQualityWarning && (
                  <Alert
                    color={brandColors.warning}
                    variant="light"
                    icon={<IconAlertTriangle size={16} />}
                    role="status"
                    aria-live="polite"
                  >
                    <Text size="sm">{INSTRUCTIONS_QUALITY_WARNING_COPY}</Text>
                  </Alert>
                )}
                {showFreePlanQualityNudge && (
                  <Alert
                    color="blue"
                    variant="light"
                    icon={<IconInfoCircle size={16} />}
                    role="status"
                    aria-live="polite"
                  >
                    <Text size="sm">
                      Spotting one-time phrasing in your default instructions is a subscriber feature.{' '}
                      <Anchor
                        component="button"
                        type="button"
                        onClick={() =>
                          openUpgrade('Default instructions quality check requires an active subscription.')
                        }
                      >
                        Upgrade to enable the check
                      </Anchor>
                      .
                    </Text>
                  </Alert>
                )}
              </Stack>

              <Group gap="sm">
                <Button
                  color={actionColors.generate}
                  leftSection={isGenerating ? <Loader size={14} color="white" /> : <IconSparkles size={14} />}
                  onClick={handleGenerateNew}
                  disabled={generateNewDisabled}
                >
                  Generate new
                </Button>
                {isEcard && isGenerating && (
                  <Button
                    size="compact-sm"
                    variant="subtle"
                    color="gray"
                    leftSection={<IconX size={14} />}
                    onClick={handleCancelStream}
                  >
                    Cancel
                  </Button>
                )}
              </Group>

              <Stack gap="xs">
                <Text size="sm" fw={600}>Rewrite instructions (this session only)</Text>
                <Text size="xs" c="dimmed">
                  Used only with Rewrite — they are not saved to {recipientDisplayName}&apos;s contact.
                </Text>
                <Textarea
                  value={rewriteInstructions}
                  onChange={(e) => setRewriteInstructions(e.currentTarget.value)}
                  placeholder='e.g. "Tone down the fishing joke" or "Mention the new puppy"'
                  autosize
                  minRows={2}
                  maxRows={5}
                  maxLength={1000}
                />
                <Button
                  color={actionColors.generate}
                  variant="light"
                  leftSection={<IconWand size={14} />}
                  onClick={handleRewrite}
                  disabled={rewriteDisabled}
                  style={{ alignSelf: 'flex-start' }}
                >
                  Rewrite
                </Button>
              </Stack>
          </Stack>

          <div className={classes.draftColumn}>
            <Stack gap="xs" className={classes.draftColumnInner}>
              {isEcard && (
                <>
                  <Text size="sm" fw={600}>Subject</Text>
                  <TextInput
                    value={subjectLine}
                    onChange={(e) => setSubjectLine(e.currentTarget.value)}
                    placeholder="Subject line"
                  />
                </>
              )}

              <Text size="sm" fw={600} mt={isEcard ? 'xs' : undefined}>
                {isEcard ? 'Greeting' : 'Message'}
              </Text>

              {isMobileLayout ? (
                <Textarea
                  value={greetingBody}
                  onChange={(e) => setGreetingBody(e.currentTarget.value)}
                  placeholder={isEcard ? 'Greeting message' : 'Card message'}
                  autosize
                  minRows={5}
                  maxRows={12}
                  maxLength={maxBodyLength}
                />
              ) : (
                <div className={classes.draftGrow}>
                  <Textarea
                    value={greetingBody}
                    onChange={(e) => setGreetingBody(e.currentTarget.value)}
                    placeholder={isEcard ? 'Greeting message' : 'Card message'}
                    maxLength={maxBodyLength}
                    classNames={{
                      root: classes.draftTextareaRoot,
                      wrapper: classes.draftTextareaWrapper,
                      input: classes.draftTextareaInput,
                    }}
                  />
                </div>
              )}

              {typeof maxBodyLength === 'number' && (
                <Text size="xs" c="dimmed" ta="right">
                  {greetingBody.length} / {maxBodyLength}
                </Text>
              )}

              {occasionHintWarnings.length > 0 && (
                <Alert color={brandColors.warning} variant="light" icon={<IconAlertTriangle size={16} />}>
                  <Text size="sm" fw={600}>Occasion wording hint</Text>
                  <Text size="sm" mt={4}>
                    The output contains phrases we often see on a different kind of card:{' '}
                    <Text component="span" inherit fw={600}>
                      {occasionHintWarnings.join(', ')}
                    </Text>
                    . You can keep it, edit it, or try again — this is a heads-up only, not a block.
                  </Text>
                </Alert>
              )}

              {error && (
                <Alert color={brandColors.danger} variant="light" icon={<IconAlertCircle size={16} />}>
                  {error}
                </Alert>
              )}

              {(lastRun || overwriteSnapshot) && (
                <Stack gap="xs" mt="md">
                  <Text size="sm" fw={600}>Or pick a quick refinement</Text>
                  <Group gap="sm" wrap="wrap" align="center">
                    {lastRun && (
                      <Anchor
                        size="xs"
                        component="button"
                        type="button"
                        onClick={handleTryAgain}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
                        c={refinementDisabled ? 'dimmed' : undefined}
                      >
                        <IconRefresh size={12} /> Try again
                      </Anchor>
                    )}
                    {REFINEMENT_CHIPS.map((chip) => (
                      <Badge
                        key={chip.label}
                        component="button"
                        type="button"
                        variant="light"
                        color={actionColors.generate}
                        style={{
                          cursor: refinementDisabled ? 'not-allowed' : 'pointer',
                          opacity: refinementDisabled ? 0.5 : 1,
                        }}
                        onClick={() => {
                          if (refinementDisabled) return;
                          handleRefinementChip(chip.label);
                        }}
                      >
                        {chip.label}
                      </Badge>
                    ))}
                    {overwriteSnapshot && (
                      <Anchor
                        size="xs"
                        component="button"
                        type="button"
                        onClick={handleRestoreEdits}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
                        c={brandColors.warning}
                      >
                        <IconRestore size={12} /> Restore my edits
                      </Anchor>
                    )}
                  </Group>
                </Stack>
              )}
            </Stack>
          </div>
        </div>
      </Paper>
    </Stack>
  );
};

/**
 * Convenience hook: copies the contact-level AI Instructions onto an
 * existing per-event guidance row. Surfaced from the Edit AI settings
 * modal "Reset to default" button (not used directly by this modal).
 */
export const useResetAiGuidance = () => {
  return useCallback(
    async (contactId: number, eventTypeId: number) => {
      await resetAiGuidanceToDefault(contactId, eventTypeId);
    },
    []
  );
};

/**
 * Resolves the typewriter-feel of streamed text without re-renders. Helper
 * exported for tests that want to simulate paced delta arrival without
 * pulling in a fake-timers dependency. Returns a promise that resolves
 * after the next paint frame.
 */
export const _waitForNextPaint = (): Promise<void> =>
  new Promise((resolve) => {
    if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(() => resolve());
    } else {
      setTimeout(resolve, 0);
    }
  });

/**
 * Sentinel re-exported so callers (e.g. tests) can avoid duplicating the
 * "More" overflow magic string.
 */
export const AI_WRITING_ASSISTANT_MORE_TONE = MORE_TONE_SENTINEL;

/** Convenience export of the action button labels used by Vitest assertions. */
export const AI_WRITING_ASSISTANT_LABELS = {
  generateNew: 'Generate new',
  rewrite: 'Rewrite',
  insert: 'Insert',
  cancel: 'Cancel',
} as const;

/** Default coaching-alert copy used by Vitest assertions. */
export const AI_WRITING_ASSISTANT_COACHING_KEYWORD = 'Notes section below';

/** Default refinement chip labels exposed for tests. */
export const AI_WRITING_ASSISTANT_REFINEMENT_LABELS = REFINEMENT_CHIPS.map((c) => c.label);

/** Default placeholder for "Restore my edits" link. */
export const AI_WRITING_ASSISTANT_RESTORE_LABEL = 'Restore my edits';

/** Re-export the icon used as the modal trigger affordance. */
export { IconCheck as AiAssistantInsertIcon };
